import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Building2, Search, Plus, ChevronLeft, ChevronRight,
  ShoppingCart, DollarSign, Package, UserPlus, X, Loader2, CheckCircle,
} from 'lucide-react';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 25;

const ComercialClientes = () => {
  const { token, API_URL } = useAuth();

  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);

  // Solicitud Alta Cliente modal
  const [showAltaModal, setShowAltaModal] = useState(false);
  const [altaForm, setAltaForm] = useState({
    razon_social: '', alias: '', calle: '', localidad: '',
    provincia: 'Madrid', c_postal: '', cif: '', telefono: '', mail: '', contacto: '',
  });
  const [altaSubmitting, setAltaSubmitting] = useState(false);
  const [altaError, setAltaError] = useState('');
  const [altaSuccess, setAltaSuccess] = useState(false);

  const totalPages = Math.ceil(totalClients / ITEMS_PER_PAGE);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(ITEMS_PER_PAGE),
      });
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      const res = await fetch(`${API_URL}/clients?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar clientes');
      const data = await res.json();
      setClients(data.clients || data.data || []);
      setTotalClients(data.total || data.totalClients || 0);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, page, searchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleAltaSubmit = async (e) => {
    e.preventDefault();
    setAltaError('');
    setAltaSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/clients/solicitud-alta`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(altaForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar solicitud');
      setAltaSuccess(true);
      setAltaForm({
        razon_social: '', alias: '', calle: '', localidad: '',
        provincia: 'Madrid', c_postal: '', cif: '', telefono: '', mail: '', contacto: '',
      });
    } catch (err) {
      setAltaError(err.message);
    } finally {
      setAltaSubmitting(false);
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setSearchTerm(val);
      setPage(1);
    }, 400));
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
            <Building2 className="w-6 h-6" style={{ color: PRIMARY }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Mis Clientes</h1>
            <p className="text-sm text-gray-500">{totalClients} clientes asignados</p>
          </div>
        </div>
        <button
          onClick={() => { setShowAltaModal(true); setAltaError(''); setAltaSuccess(false); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors hover:opacity-90"
          style={{ backgroundColor: PRIMARY }}
        >
          <UserPlus size={16} /> Solicitar Alta Cliente
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por razón social, alias o CIF..."
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ focusRingColor: PRIMARY }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón Social</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alias</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CIF</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: PRIMARY }} />
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No se encontraron clientes</p>
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client.razon_social}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.alias || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.cif || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.telefono || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/pedidos/nuevo?cliente=${client.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-white transition-colors hover:opacity-90"
                          style={{ backgroundColor: PRIMARY }}
                          title="Nuevo Pedido"
                        >
                          <Plus size={12} /> Pedido
                        </Link>
                        <Link
                          to={`/comercial/pedidos-cliente/${client.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                          title="Ver Pedidos"
                        >
                          <ShoppingCart size={12} /> Pedidos
                        </Link>
                        <Link
                          to={`/comercial/precios-cliente/${client.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                          title="Ver Precios"
                        >
                          <DollarSign size={12} /> Precios
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, totalClients)} de {totalClients}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-700 font-medium px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Solicitar Alta Cliente */}
      {showAltaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowAltaModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Datos de nuevo cliente</h2>
              <button onClick={() => setShowAltaModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            {altaSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#10B981' }} />
                <p className="text-lg font-semibold text-gray-900 mb-2">Solicitud enviada correctamente</p>
                <p className="text-sm text-gray-500 mb-6">El administrador revisará la solicitud de alta.</p>
                <button
                  onClick={() => setShowAltaModal(false)}
                  className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors hover:opacity-90"
                  style={{ backgroundColor: PRIMARY }}
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <>
                {altaError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {altaError}
                  </div>
                )}

                <form onSubmit={handleAltaSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Razón Social *</label>
                    <input
                      type="text"
                      value={altaForm.razon_social}
                      onChange={(e) => setAltaForm({ ...altaForm, razon_social: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
                    <input
                      type="text"
                      value={altaForm.alias}
                      onChange={(e) => setAltaForm({ ...altaForm, alias: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Calle *</label>
                    <input
                      type="text"
                      value={altaForm.calle}
                      onChange={(e) => setAltaForm({ ...altaForm, calle: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Localidad *</label>
                    <input
                      type="text"
                      value={altaForm.localidad}
                      onChange={(e) => setAltaForm({ ...altaForm, localidad: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                    <select
                      value={altaForm.provincia}
                      onChange={(e) => setAltaForm({ ...altaForm, provincia: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    >
                      {PROVINCIAS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código Postal *</label>
                    <input
                      type="text"
                      value={altaForm.c_postal}
                      onChange={(e) => setAltaForm({ ...altaForm, c_postal: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      minLength={5}
                      maxLength={5}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CIF *</label>
                    <input
                      type="text"
                      value={altaForm.cif}
                      onChange={(e) => setAltaForm({ ...altaForm, cif: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                    <input
                      type="tel"
                      value={altaForm.telefono}
                      onChange={(e) => setAltaForm({ ...altaForm, telefono: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                    <input
                      type="email"
                      value={altaForm.mail}
                      onChange={(e) => setAltaForm({ ...altaForm, mail: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
                    <input
                      type="text"
                      value={altaForm.contacto}
                      onChange={(e) => setAltaForm({ ...altaForm, contacto: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2 flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAltaModal(false)}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={altaSubmitting}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: PRIMARY }}
                    >
                      {altaSubmitting && <Loader2 size={16} className="animate-spin" />}
                      Continuar
                    </button>
                  </div>

                  <p className="md:col-span-2 text-xs text-gray-400">* Campos Obligatorios</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PROVINCIAS = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 'Badajoz', 'Barcelona',
  'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ciudad Real', 'Córdoba', 'Cuenca',
  'Gerona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva', 'Huesca', 'Islas Baleares', 'Jaén',
  'La Coruña', 'La Rioja', 'Las Palmas', 'León', 'Lérida', 'Lugo', 'Madrid', 'Málaga', 'Murcia',
  'Navarra', 'Orense', 'Palencia', 'Pontevedra', 'Salamanca', 'Santa Cruz de Tenerife', 'Segovia',
  'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid', 'Vizcaya',
  'Zamora', 'Zaragoza',
];

export default ComercialClientes;
